import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import { SimpleShowLayout, FunctionField, DateField, usePermissions, useTranslate } from 'react-admin';
import DeviceErrorReportList from '@/components/device/DeviceErrorReportList.jsx';
import ServiceHistory from '@/components/service/ServiceHistory.jsx';
import DeviceLogs from '@/components/device/DeviceLogs.jsx';
import DeviceLogsHeader from '@/components/device/DeviceLogsHeader.jsx';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';
import { useLocation } from 'react-router-dom';

const DeviceDetailsCollapse = ({
    isSummaryOpen,
    setIsSummaryOpen,
    record,
    UnassignedField,
    elementId,
    isErrorsOpen,
    setIsErrorsOpen,
    isServiceHistoryOpen,
    setIsServiceHistoryOpen,
    isLogsOpen,
    setIsLogsOpen,
}) => {
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const isAdminOrBok = useMemo(() => permissions.isAdmin || permissions.isBok, [permissions]);
    const [logsRequested, setLogsRequested] = useState(false);
    const location = useLocation();
    const isOnNewDevicePage = location.pathname.includes('/new-device');
    const logsHeaderContent = <DeviceLogsHeader deviceId={elementId} logsRequested={logsRequested} />;

    return (
        <Box className='details-sample-list device-details-collapse-wrapper'>
            <CollapsibleSection
                title={translate('resources.device.labels.deviceDetails')}
                isOpen={isSummaryOpen}
                onToggle={() => setIsSummaryOpen(!isSummaryOpen)}
            >
                <Box className='device-details-collapse'>
                    <Box className='details-date' sx={{ flex: 1 }}>
                        <SimpleShowLayout record={record} className='details-date'>
                            <FunctionField
                                label={translate('resources.device.fields.productionDate')}
                                source='productionDate'
                                render={rec => {
                                    const value = rec.productionDate;
                                    return value ? <DateField source='productionDate' options={{ dateStyle: 'short' }} record={rec} /> : <UnassignedField />;
                                }}
                            />
                            <FunctionField
                                label={translate('resources.device.fields.warrantyDate')}
                                source='warrantyDate'
                                render={rec => {
                                    const value = rec.warrantyDate;
                                    return value ? <DateField source='warrantyDate' options={{ dateStyle: 'short' }} record={rec} /> : <UnassignedField />;
                                }}
                            />
                            <FunctionField
                                label={translate('resources.device.fields.deploymentDate')}
                                source='deploymentDate'
                                render={rec => {
                                    const value = rec.deploymentDate;
                                    return value ? <DateField source='deploymentDate' options={{ dateStyle: 'short' }} record={rec} /> : <UnassignedField />;
                                }}
                            />
                            {isAdminOrBok && record.collectionPoint && ['Kaucja', 'Reselekt'].includes(record.collectionPoint.operator) && (
                                <FunctionField
                                    label={translate('resources.device.fields.registrationDate')}
                                    source='registrationDate'
                                    render={rec => {
                                        const value = rec.registrationDate;
                                        return value ? (
                                            <DateField source='registrationDate' options={{ dateStyle: 'short' }} record={rec} />
                                        ) : (
                                            <UnassignedField label={translate('resources.device.fields.notRegistered')} />
                                        );
                                    }}
                                />
                            )}
                        </SimpleShowLayout>
                    </Box>
                    <Box className='details-date' sx={{ flex: 1 }}>
                        <SimpleShowLayout record={record}>
                            <FunctionField
                                source='hardwareSoc'
                                label={translate('resources.device.fields.electronicslVersion')}
                                render={rec => {
                                    const parts = [];
                                    if (rec.hardwareSoc) parts.push(`SOC: v${rec.hardwareSoc}`);
                                    if (rec.hardwareMcu) parts.push(`MCU: v${rec.hardwareMcu}`);
                                    return parts.length ? parts.join(', ') : translate('resources.device.fields.notSpecified');
                                }}
                            />
                            <FunctionField
                                source='softwareSoc'
                                label={translate('resources.device.fields.softwareVersion')}
                                render={rec => {
                                    const parts = [];
                                    if (rec.softwareSoc) parts.push(`SOC: v${rec.softwareSoc}`);
                                    if (rec.softwareMcu) parts.push(`MCU: v${rec.softwareMcu}`);
                                    return parts.length ? parts.join(', ') : translate('resources.device.fields.notSpecified');
                                }}
                            />
                            <FunctionField
                                source='coordinator'
                                label={translate('resources.serviceRequest.labels.coordinator')}
                                render={rec =>
                                    rec.coordinator && (rec.coordinator.firstName || rec.coordinator.lastName)
                                        ? `${rec.coordinator.firstName ?? ''} ${rec.coordinator.lastName ?? ''}`.trim()
                                        : translate('resources.device.fields.notSpecified')
                                }
                            />
                            <FunctionField
                                source='coordinator.email'
                                label={translate('resources.device.labels.coordinatorEmail')}
                                render={rec => rec.coordinator?.email || translate('resources.device.fields.notSpecified')}
                            />
                        </SimpleShowLayout>
                    </Box>
                </Box>
            </CollapsibleSection>
            {(isAdminOrBok || permissions.isService || permissions.isService) && (
                <CollapsibleSection
                    title={translate('resources.device.labels.lastErrors')}
                    isOpen={isErrorsOpen}
                    onToggle={() => setIsErrorsOpen(!isErrorsOpen)}
                >
                    <Box className='device-details-collapse'>
                        <DeviceErrorReportList deviceId={elementId} shouldExtend={true} />
                    </Box>
                </CollapsibleSection>
            )}
            <CollapsibleSection
                title={translate('resources.device.labels.serviceHistory')}
                isOpen={isServiceHistoryOpen}
                onToggle={() => setIsServiceHistoryOpen(!isServiceHistoryOpen)}
            >
                <Box className='device-details-collapse'>
                    <ServiceHistory deviceId={elementId} />
                </Box>
            </CollapsibleSection>
            {(isAdminOrBok || permissions.isService) && (
                <CollapsibleSection
                    title={translate('resources.device.labels.logs')}
                    isOpen={isLogsOpen}
                    onToggle={() => setIsLogsOpen(!isLogsOpen)}
                    headerContent={logsHeaderContent}
                >
                    <Box className='device-details-collapse'>
                        <DeviceLogs deviceId={elementId} onLogsRequestedChange={setLogsRequested} />
                    </Box>
                </CollapsibleSection>
            )}
            <ChangeHistory resource={isOnNewDevicePage ? 'new-device' : 'device'} recordId={elementId} fieldsSource='resources.device.fields' />
        </Box>
    );
};

export default DeviceDetailsCollapse;
